import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Film, X, ChevronLeft, ChevronRight,
  HeartPulse, Siren, Anchor, Radio, Compass, Waves, Ship, ArrowRight,
  CloudSun, LifeBuoy, MessageSquare, Plane,
} from "lucide-react";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PSC1Card } from "./FloatingPSC1Info";
import { AccidentCard } from "./FloatingAccidentSimulation";

type Frame = {
  key: string;
  label: string;
  category: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  render: () => React.ReactNode;
};

// ─── Pathway frame helper ────────────────────────────────────────────────
type PathFrameProps = {
  title: string;
  subtitle: string;
  accent: string; // tailwind text color class
  border: string; // tailwind border color class
  icon: React.ComponentType<{ className?: string }>;
  level: string;
  bullets: string[];
  nextStep: string;
  cta?: { label: string; to: string };
};

const PathFrame = ({ title, subtitle, accent, border, icon: Icon, level, bullets, nextStep, cta }: PathFrameProps) => (
  <Card className={`w-full shadow-2xl ${border} bg-gradient-to-br from-background to-background/95`}>
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-slate-900 to-black flex items-center justify-center shadow-lg ${border}`}>
          <Icon className={`h-5 w-5 ${accent}`} />
        </div>
        <div className="flex-1">
          <CardTitle className={`text-base ${accent}`}>{title}</CardTitle>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <Badge variant="outline" className={`text-[10px] ${accent} ${border}`}>{level}</Badge>
      </div>
    </CardHeader>
    <CardContent className="space-y-3 pt-0">
      <ul className="space-y-1.5">
        {bullets.map((b, i) => (
          <li key={i} className="text-xs text-muted-foreground leading-relaxed flex items-start gap-2">
            <span className={`${accent} mt-0.5`}>•</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
        <div className="flex items-start gap-2">
          <ArrowRight className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-primary">Next Step</p>
            <p className="text-xs text-muted-foreground mt-1">{nextStep}</p>
          </div>
        </div>
      </div>
      {cta && (
        <Button asChild variant="outline" size="sm" className={`w-full text-xs ${border}`}>
          <Link to={cta.to}>
            <Compass className="h-3.5 w-3.5 mr-1.5" /> {cta.label}
          </Link>
        </Button>
      )}
    </CardContent>
  </Card>
);

// ─── Reel order: Fluvial → Permis Côtier → CRR → BPJEPS Jet-ski → Captain ───
const FRAMES: Frame[] = [
  {
    key: "psc1",
    label: "PSC1",
    category: "Spécial",
    icon: HeartPulse,
    color: "text-red-400",
    render: () => <PSC1Card />,
  },
  {
    key: "accident",
    label: "Accident",
    category: "Spécial",
    icon: Siren,
    color: "text-orange-400",
    render: () => <AccidentCard />,
  },
  {
    key: "fluvial",
    label: "Fluvial",
    category: "Niveaux",
    icon: Waves,
    color: "text-sky-400",
    render: () => (
      <PathFrame
        title="Permis Fluvial"
        subtitle="Eaux intérieures — canaux & rivières"
        accent="text-sky-400"
        border="border-sky-500/30"
        icon={Waves}
        level="Niveau 1"
        bullets={[
          "Obligatoire à partir de 6 CV pour piloter sur les voies navigables intérieures.",
          "Théorie : balisage fluvial, écluses, signalisation, priorités, VNF.",
          "Pratique : 3h30 minimum d'enseignement sur péniche / vedette.",
          "Idéal première porte d'entrée vers les métiers de l'eau (péniche-hôtel, batellerie).",
        ]}
        nextStep="Réviser le balisage fluvial (cardinal vs latéral) avec Captain ROCA — 5 min/jour."
        cta={{ label: "Ouvrir le module fluvial", to: "/permis-academy" }}
      />
    ),
  },
  {
    key: "cotier",
    label: "Côtier",
    category: "Niveaux",
    icon: Compass,
    color: "text-cyan-400",
    render: () => (
      <PathFrame
        title="Permis Côtier"
        subtitle="Mer — jusqu'à 6 milles d'un abri"
        accent="text-cyan-400"
        border="border-cyan-500/30"
        icon={Compass}
        level="Niveau 2"
        bullets={[
          "Pré-requis pour louer ou piloter un bateau à moteur en mer.",
          "Théorie : RIPAM, balisage maritime, météo, sécurité, VHF de base.",
          "Pratique : manœuvres au port, homme à la mer, navigation au cap.",
          "Tremplin direct vers le BPJEPS Motonautisme (jet-ski) et le yachting.",
        ]}
        nextStep="Enchaîner 1 quiz Côtier puis lire les zones CRR 1→4 — 10 min."
        cta={{ label: "Quiz Côtier", to: "/permis-academy" }}
      />
    ),
  },
  {
    key: "crr",
    label: "CRR",
    category: "Niveaux",
    icon: Radio,
    color: "text-amber-400",
    render: () => (
      <PathFrame
        title="CRR — Radio VHF"
        subtitle="Certificat Restreint de Radiotéléphoniste"
        accent="text-amber-400"
        border="border-amber-500/30"
        icon={Radio}
        level="Niveau 3"
        bullets={[
          "Obligatoire pour utiliser une VHF marine fixe (DSC / ASN).",
          "Procédures MAYDAY, PAN-PAN, SÉCURITÉ — alphabet OACI, MMSI.",
          "Examen ANFR : 30 questions, 1 erreur grave éliminatoire.",
          "Reconnu CEPT — valable dans toute l'Europe maritime.",
        ]}
        nextStep="Lancer 10 flashcards CRR aujourd'hui — viser 80 % de bonnes réponses."
        cta={{ label: "Flash CRR", to: "/crr-flash" }}
      />
    ),
  },
  {
    key: "bpjeps",
    label: "BPJEPS",
    category: "Niveaux",
    icon: Anchor,
    color: "text-emerald-400",
    render: () => (
      <PathFrame
        title="BPJEPS Motonautisme"
        subtitle="Moniteur jet-ski / scooter des mers"
        accent="text-emerald-400"
        border="border-emerald-500/30"
        icon={Anchor}
        level="Niveau 4"
        bullets={[
          "Diplôme d'État pour enseigner le jet-ski contre rémunération.",
          "Pré-requis : PSC1, Permis Côtier, 16 ans, TEP (tests d'entrée).",
          "Formation : ~600 h alternance — pédagogie, sécurité, mécanique.",
          "Débouchés : bases nautiques, clubs, location encadrée, randonnées.",
        ]}
        nextStep="Vérifier ta liste TEP : PSC1 ✓, Côtier ✓, certif. médical, attestation 100 m nage."
        cta={{ label: "Roadmap BPJEPS", to: "/bpjeps-tep-roadmap" }}
      />
    ),
  },
  {
    key: "captain",
    label: "Captain",
    category: "Niveaux",
    icon: Ship,
    color: "text-gold-copper",
    render: () => (
      <PathFrame
        title="Capitaine 200 → 3000"
        subtitle="Yachting professionnel — Antibes, Palma, DOM-TOM"
        accent="text-amber-300"
        border="border-amber-400/40"
        icon={Ship}
        level="Niveau 5"
        bullets={[
          "Capitaine 200 : commandement yacht ≤ 200 GT, jusqu'à 200 milles.",
          "Capitaine 500 / 3000 : grands yachts internationaux, équipage.",
          "Pré-requis : temps de mer (sea time), STCW, médical ENG1.",
          "Voie progressive : Deckhand → Bosun → OOW → Chief Officer → Captain.",
        ]}
        nextStep="Commencer un logbook (sea time) DÈS le premier jour à bord — chaque heure compte."
        cta={{ label: "Carrières maritimes", to: "/maritime-careers" }}
      />
    ),
  },
  {
    key: "vhf",
    label: "VHF",
    category: "Mémo",
    icon: Radio,
    color: "text-indigo-400",
    render: () => (
      <PathFrame
        title="VHF — Mémo procédures"
        subtitle="Canal 16 · MAYDAY · PAN-PAN · SÉCURITÉ"
        accent="text-indigo-400"
        border="border-indigo-500/30"
        icon={Radio}
        level="Mémo"
        bullets={[
          "Canal 16 = veille permanente, urgence & appel. Repasser au canal de travail dès contact.",
          "MAYDAY × 3 : danger grave et imminent pour la vie. Seul le commandant l'émet.",
          "PAN-PAN × 3 : urgence sans danger immédiat (avarie, blessé léger).",
          "SÉCURITÉ × 3 : message de sécurité (météo, obstacle, dérive).",
          "Alphabet OACI : Alpha Bravo Charlie… toujours épeler nom + MMSI.",
        ]}
        nextStep="Mémoriser l'ordre du message MAYDAY : Position → Nature → Nombre → Assistance."
        cta={{ label: "Réviser le CRR", to: "/crr-flash" }}
      />
    ),
  },
  {
    key: "balisage",
    label: "Balisage",
    category: "Mémo",
    icon: Compass,
    color: "text-teal-400",
    render: () => (
      <PathFrame
        title="Balisage — Lecture rapide"
        subtitle="Latéral · Cardinal · Spécial · Danger isolé"
        accent="text-teal-400"
        border="border-teal-500/30"
        icon={Compass}
        level="Mémo"
        bullets={[
          "Latéral région A (Europe) : rouge à BÂBORD en entrant au port, vert à TRIBORD.",
          "Cardinal N : 2 cônes pointe en haut ↑↑ — passer au Nord de la marque.",
          "Cardinal S : 2 cônes pointe en bas ↓↓ — passer au Sud.",
          "Cardinal E : cônes base à base ◇ — Est. Cardinal O : pointes à pointes ✕ — Ouest.",
          "Danger isolé : 2 sphères noires. Marque spéciale : jaune en croix.",
        ]}
        nextStep="Dessiner les 4 cardinales de mémoire AVANT chaque session de quiz."
        cta={{ label: "Zones CRR 1→4", to: "/crr-zone-1" }}
      />
    ),
  },
  {
    key: "meteo",
    label: "Météo",
    category: "Mémo",
    icon: CloudSun,
    color: "text-blue-400",
    render: () => (
      <PathFrame
        title="Météo marine — Flash"
        subtitle="BMS · Beaufort · Douglas · lecture rapide"
        accent="text-blue-400"
        border="border-blue-500/30"
        icon={CloudSun}
        level="Mémo"
        bullets={[
          "BMS = Bulletin Météo Spécial : vent ≥ F7 ou phénomène dangereux. Ne JAMAIS sortir.",
          "Échelle Beaufort : F4 (jolie brise, 11-16 nœuds) = limite confort jet-ski débutant.",
          "État de la mer (Douglas) : 0 calme · 3 peu agitée · 5 forte · 7 très grosse.",
          "Avant chaque sortie : consulter Météo-France Marine + Windy + VHF canal 63 (CROSS).",
          "Règle d'or : en cas de doute, on ne sort pas — le bateau attend, pas l'équipage.",
        ]}
        nextStep="Bookmark Météo-France Marine et lire le BMS de TA zone aujourd'hui."
        cta={{ label: "Guide météo marine", to: "/meteo-marine-guide" }}
      />
    ),
  },
  {
    key: "ocean",
    label: "Océan",
    category: "Spécial",
    icon: LifeBuoy,
    color: "text-emerald-400",
    render: () => (
      <PathFrame
        title="Mission Océan"
        subtitle="Sea Shepherd · conservation · responsabilité"
        accent="text-emerald-400"
        border="border-emerald-500/30"
        icon={LifeBuoy}
        level="Mission"
        bullets={[
          "Travailler en mer = devenir gardien de l'océan, pas seulement utilisateur.",
          "Réduire le sillage en zone Posidonie (Méditerranée) — protéger les herbiers.",
          "Mouillage écologique : bouées plutôt qu'ancres sur fonds sensibles.",
          "Signaler échouages mammifères marins : Réseau National Échouages (RNE).",
          "Sea Shepherd & ONG conservation : voies de bénévolat & carrières alignées.",
        ]}
        nextStep="Choisir UNE éco-action à appliquer dès ta prochaine sortie en mer."
        cta={{ label: "Carrières maritimes", to: "/maritime-careers" }}
      />
    ),
  },
  {
    key: "briefing",
    label: "Briefing",
    category: "Modules",
    icon: MessageSquare,
    color: "text-cyan-400",
    render: () => (
      <PathFrame
        title="Briefing client"
        subtitle="Communication & sécurité avant départ"
        accent="text-cyan-400"
        border="border-cyan-500/30"
        icon={MessageSquare}
        level="Module"
        bullets={[
          "Présentation : équipe, zone de navigation, météo du jour.",
          "Consignes de sécurité essentielles (gilet, coupe-circuit, distances).",
          "Signaux & gestes de communication eau ↔ bateau.",
          "Vérification combinaison & ajustement gilet.",
          "Question-réponse avec le client avant de larguer.",
        ]}
        nextStep="Répéter ton briefing à voix haute en 2 min — clair, calme, structuré."
        cta={{ label: "Voir le calendrier BPJEPS", to: "/bpjeps-motonautisme" }}
      />
    ),
  },
  {
    key: "jet-selle",
    label: "Jet à selle",
    category: "Modules",
    icon: Waves,
    color: "text-sky-400",
    render: () => (
      <PathFrame
        title="Jet à selle"
        subtitle="Pilotage assis — randonnée & initiation"
        accent="text-sky-400"
        border="border-sky-500/30"
        icon={Waves}
        level="Module"
        bullets={[
          "Posture assise & équilibre — bassin centré, regard loin.",
          "Démarrage progressif, virages inclinés, arrêt d'urgence.",
          "Lecture du plan d'eau (vagues, courants, autres usagers).",
          "Encadrement randonnée : ouvrir la voie, fermer la marche.",
          "Sécurité : distance 50 m, vitesse adaptée à la zone.",
        ]}
        nextStep="Faire 1 parcours en jet à selle avec arrêt d'urgence chronométré."
        cta={{ label: "Voir le calendrier BPJEPS", to: "/bpjeps-motonautisme" }}
      />
    ),
  },
  {
    key: "engins-tractes",
    label: "Engins tractés",
    category: "Modules",
    icon: Anchor,
    color: "text-cyan-300",
    render: () => (
      <PathFrame
        title="Engins tractés"
        subtitle="Bouée · ski · wake — pilotage bateau tracteur"
        accent="text-cyan-300"
        border="border-cyan-400/30"
        icon={Anchor}
        level="Module"
        bullets={[
          "Pilotage du bateau tracteur : trajectoires fluides, anticipation.",
          "Largage & récupération des clients en sécurité.",
          "Vitesse adaptée à l'engin (bouée < ski < wake).",
          "Communication équipier ↔ pilote (gestes codifiés).",
          "Gestion de la chute : arrêt, demi-tour, approche au vent.",
        ]}
        nextStep="Réviser les 5 signes main standards engins tractés avant la prochaine sortie."
        cta={{ label: "Voir le calendrier BPJEPS", to: "/bpjeps-motonautisme" }}
      />
    ),
  },
  {
    key: "flyboard",
    label: "Flyboard",
    category: "Modules",
    icon: Plane,
    color: "text-sky-300",
    render: () => (
      <PathFrame
        title="Flyboard"
        subtitle="Initiation & encadrement vol au-dessus de l'eau"
        accent="text-sky-300"
        border="border-sky-400/30"
        icon={Plane}
        level="Module"
        bullets={[
          "Combinaison intégrale obligatoire (protection chute).",
          "Décollage : jambes TENDUES, corps ALIGNÉ, pieds À PLAT.",
          "Pointe des pieds : on DESCEND et on avance.",
          "Direction gérée par le client — on guide, on ne pilote pas.",
          "Sécurité : zone dégagée de 50 m, jamais au-dessus du bateau.",
        ]}
        nextStep="Mémoriser les 3 mots-clés décollage : TENDUES · ALIGNÉ · À PLAT."
        cta={{ label: "Briefing Flyboard complet", to: "/briefings" }}
      />
    ),
  },
];


// Film perforations strip (top or bottom)
const Perforations = () => (
  <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-black/80">
    {Array.from({ length: 14 }).map((_, i) => (
      <span
        key={i}
        className="w-3 h-3 sm:w-4 sm:h-4 rounded-[3px] bg-background/90 shadow-inner shrink-0"
      />
    ))}
  </div>
);

const FILTER_OPTIONS = ["Tous", "Modules", "Niveaux", "Mémo", "Spécial"] as const;
type FilterOption = (typeof FILTER_OPTIONS)[number];

/**
 * Single floating cinema-film-reel that groups every floating tool into one
 * horizontal carousel — like a strip of film frames scrolling past.
 */
export const FloatingFilmReel = () => {
  const [open, setOpen] = useState(false);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [filter, setFilter] = useState<FilterOption>("Tous");

  const filteredFrames = useMemo(
    () => (filter === "Tous" ? FRAMES : FRAMES.filter((f) => f.category === filter)),
    [filter]
  );

  // Reset to first frame when filter changes
  useEffect(() => {
    setCurrent(0);
    if (api) api.scrollTo(0);
  }, [filter, api]);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  // Keyboard navigation when overlay is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); api?.scrollNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); api?.scrollPrev(); }
      else if (e.key === "Home") { e.preventDefault(); api?.scrollTo(0); }
      else if (e.key === "End") { e.preventDefault(); api?.scrollTo(filteredFrames.length - 1); }
      else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, api, filteredFrames.length]);

  return (
    <>
      {/* Trigger: film reel button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-4 z-50 group flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-slate-900 to-black text-white shadow-2xl shadow-black/40 border-2 border-amber-400/60 hover:scale-110 hover:border-amber-400 transition-all duration-300"
        aria-label="Ouvrir la pochette moniteur — pellicule"
        title="Pochette moniteur — pellicule"
      >
        <Film className="h-6 w-6 text-amber-300 group-hover:rotate-180 transition-transform duration-700" />
        <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 bg-amber-400 rounded-full flex items-center justify-center text-[10px] font-bold text-black border-2 border-background">
          {FRAMES.length}
        </span>
        <div className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-background/95 backdrop-blur-sm border border-amber-400/30 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none whitespace-nowrap shadow-xl">
          <p className="text-sm font-medium text-amber-300">Pochette moniteur</p>
          <p className="text-xs text-muted-foreground">Pellicule — PSC1 · Accident · Briefing · Jet à selle · Engins tractés · Flyboard</p>
        </div>
      </button>

      {/* Cinema reel overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full sm:w-auto sm:max-w-[480px] max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={() => setOpen(false)}
              className="absolute -top-3 -right-3 z-20 p-2 rounded-full bg-background border border-amber-400/40 shadow-xl hover:bg-secondary/60 transition-colors"
              aria-label="Fermer la pellicule"
            >
              <X className="h-4 w-4" />
            </button>

            {/* Header strip with frame label */}
            <div className="px-4 py-2 bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-amber-500/20 border-x border-t border-amber-400/30 rounded-t-lg flex items-center gap-2">
              <Film className="h-4 w-4 text-amber-300" />
              <span className="text-xs font-mono uppercase tracking-[0.2em] text-amber-300">
                Reel · Frame {current + 1} / {filteredFrames.length}
              </span>
              <span className="ml-auto text-[10px] font-mono text-amber-300/70">
                {filteredFrames[current]?.label}
              </span>
            </div>

            {/* Category filters */}
            <div className="px-3 py-2 bg-black/80 border-x border-amber-400/20 flex flex-wrap justify-center gap-1.5">
              {FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  onClick={() => setFilter(opt)}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider border transition-all ${
                    filter === opt
                      ? "bg-amber-400 text-black border-amber-400"
                      : "bg-background/60 text-amber-300/80 border-amber-400/30 hover:bg-background hover:text-amber-300"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>

            {/* Top perforations */}
            <Perforations />

            {/* The film carousel */}
            <div
              className="bg-black/90 border-x border-amber-400/20 overflow-hidden touch-pan-y"
              role="region"
              aria-roledescription="carousel"
              aria-label="Pellicule moniteur"
            >
              <Carousel
                opts={{ loop: true, align: "center", dragFree: false }}
                setApi={setApi}
                className="w-full"
              >
                <CarouselContent className="ml-0">
                  {filteredFrames.map((frame, i) => (
                    <CarouselItem
                      key={frame.key}
                      className="pl-0 basis-full"
                      role="group"
                      aria-roledescription="slide"
                      aria-label={`${i + 1} sur ${filteredFrames.length} — ${frame.label}`}
                    >
                      <div className="p-3 max-h-[55vh] sm:max-h-[68vh] overflow-y-auto overscroll-contain">
                        {frame.render()}
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>

            {/* Bottom perforations */}
            <Perforations />

            {/* Controls strip — one icon at a time */}
            <div className="px-3 sm:px-4 py-3 bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-amber-500/20 border-x border-b border-amber-400/30 rounded-b-lg flex items-center justify-between gap-2 sm:gap-3">
              <button
                onClick={() => api?.scrollPrev()}
                className="shrink-0 p-2 rounded-full bg-background/70 hover:bg-background focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:outline-none border border-amber-400/30 transition-colors"
                aria-label={`Image précédente (${filteredFrames[(current - 1 + filteredFrames.length) % filteredFrames.length]?.label})`}
              >
                <ChevronLeft className="h-4 w-4 text-amber-300" />
              </button>

              {/* Single active icon + label */}
              <div className="flex-1 min-w-0 flex items-center justify-center gap-2" aria-live="polite">
                {(() => {
                  const f = filteredFrames[current];
                  if (!f) return null;
                  const Icon = f.icon;
                  return (
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400 text-black border border-amber-300 max-w-full">
                      <Icon className={`h-4 w-4 shrink-0 text-black`} />
                      <span className="text-xs font-mono uppercase tracking-wider truncate">{f.label}</span>
                      <span className="text-[10px] font-mono opacity-70 shrink-0">
                        {current + 1}/{filteredFrames.length}
                      </span>
                    </div>
                  );
                })()}
              </div>

              <button
                onClick={() => api?.scrollNext()}
                className="shrink-0 p-2 rounded-full bg-background/70 hover:bg-background focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:outline-none border border-amber-400/30 transition-colors"
                aria-label={`Image suivante (${filteredFrames[(current + 1) % filteredFrames.length]?.label})`}
              >
                <ChevronRight className="h-4 w-4 text-amber-300" />
              </button>
            </div>

            {/* Dot indicators for quick jump */}
            <div className="flex items-center justify-center gap-1.5 mt-2 flex-wrap px-2" role="tablist" aria-label="Aller à une image">
              {filteredFrames.map((f, i) => {
                const active = i === current;
                return (
                  <button
                    key={f.key}
                    role="tab"
                    aria-selected={active}
                    aria-label={`Image ${i + 1} : ${f.label}`}
                    tabIndex={active ? 0 : -1}
                    onClick={() => api?.scrollTo(i)}
                    className={`h-2 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:outline-none ${
                      active ? "w-6 bg-amber-400" : "w-2 bg-background/40 hover:bg-background/70 border border-border/40"
                    }`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingFilmReel;
